import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  LINK_UNDERLINE_OFFSET,
  LINK_UNDERLINE_THICKNESS,
  brandText,
  brandTextHover,
  navSelected,
} from '@repo/ui'

import { BookOpenIcon } from 'lucide-react'

import { EmptyState, TermProse } from 'components'

import { findDoc } from 'docs'
import { useCustomerDocs } from 'docs/useCustomerDocs'
import { DocsLayout } from 'pages/support/DocPage/DocsLayout'
import { DocToc } from 'pages/support/DocPage/DocToc'
import { ANCHOR_OFFSET } from 'pages/support/DocPage/constants'
import { GLOSSARY_NAV_VALUE } from 'pages/support/DocPage/DocSectionNav'

import {
  GLOSSARY_KINDS,
  GLOSSARY_TAGS,
  glossaryByKind,
  glossaryByTag,
  glossaryHref,
  glossaryTerm,
  type GlossaryEntry,
} from 'docs/glossary'
import { useGlossaryCorpus } from 'docs/GlossaryProvider'


const TermCard = ({
  entry,
  active,
  flash,
  reduceMotion,
}: {
  entry: GlossaryEntry
  active: boolean
  /** True for the deep-linked term, for the moment after arrival. See FLASH_MS. */
  flash: boolean
  reduceMotion: boolean
}) => {
  const theme = useTheme()
  const ref = useRef<HTMLDivElement | null>(null)
  const corpus = useGlossaryCorpus()

  /*
    `seeAlso` resolved against THIS READER's corpus, dropping what does not resolve.

    This used to render the id de-hyphenated as a fallback, on the reasoning that a
    dangling reference should stay visible as an authoring mistake. That reasoning was
    sound while the corpus was the entire glossary — and it inverts once the corpus is
    audience-scoped. The publisher build-enforces that every `seeAlso` resolves against
    the FULL glossary, so a genuinely dangling id cannot reach production; the only way
    the fallback can now fire is a reference pointing ABOVE the reader's floor. Printing
    the id there would render "tag family" onto a brand page — leaking the name of the one
    term §44 says a customer must never see, in the branch written to be helpful.

    So: drop, exactly as `TermOverlay` and the consumer app already do. Dropping is also
    what makes the graph safe to author upward, which the entries genuinely need — a
    consumer status links to the brand-only statuses it contrasts with.
  */
  const relatedEntries = useMemo(
    () =>
      (entry.seeAlso ?? [])
        .map(id => glossaryTerm(id, corpus))
        .filter((e): e is GlossaryEntry => Boolean(e)),
    [entry, corpus],
  )

  /*
    ARRIVAL SCROLL. The hash alone does not move the page here, and that is not an
    oversight anyone would spot from the code: a `#anchor` scrolls natively only on a real
    document navigation, and every route into this page is a react-router `Link`, which
    pushes state without one. So arriving from a tooltip footer — the entire point of
    §44's deep links — landed you at the TOP of a 14-entry page with the term you asked
    for somewhere below the fold, and the `active` marker below marked a row you could not
    see. Verified in the browser before changing it.

    Runs on `active` rather than on mount so that clicking a `seeAlso` link from this very
    page (a hash-only change, which re-renders without remounting) also moves.

    `behavior: 'auto'`, NOT 'smooth', and this is measured rather than preference. Smooth
    scrolling is an ANIMATION, so it depends on the frame loop — and in the preview harness
    these surfaces are verified in, the frame loop does not run: `scrollIntoView({behavior:
    'smooth'})` on this element left `scrollTop` at 0 after 1200ms, while 'auto' landed it
    at exactly the 96px `scrollMarginTop` below. (Same reason `DocToc`'s own smooth
    `onSelect` does not move the page there either — pre-existing, not introduced here.)
    An instant jump is also what a native `#anchor` does, and over ~7900px of glossary a
    smooth scroll is a long ride past content nobody asked to see.
  */
  useEffect(() => {
    if (!active) return
    ref.current?.scrollIntoView({ behavior: 'auto', block: 'start' })
  }, [active])

  return (
    <Box
      // The id IS the anchor every tooltip footer links to (§44). scroll-margin is what puts
      // the landed term BELOW the docs header band rather than jammed against the top of the
      // scroll container — `scrollIntoView({ block: 'start' })` honours it, so the offset lives
      // on the target rather than being arithmetic at the call site. ANCHOR_OFFSET is the same
      // constant the guide headings and the scroll-spy use, so every anchor in the docs lands
      // on one line; this was a hard-coded 96 that happened to agree with it.
      id={entry.id}
      ref={ref}
      sx={{
        scrollMarginTop: `${ANCHOR_OFFSET}px`,
        py: 4,
        borderTop: `1px solid ${theme.palette.divider}`,
        /*
          Two markers doing two different jobs.

          The BAR is permanent: minutes later it still answers "which of these was I sent to",
          and it is a position/shape cue rather than a colour one, so it survives a reader who
          cannot distinguish the tint.

          The WASH is the arrival signal, and it exists because the bar alone did not do that
          job — a 2px rule on a long page is not something the eye lands on, which is exactly
          what was reported. It is transient by design: a highlight that never clears makes the
          page look broken on the next scroll, and it would compete with the bar for the meaning
          the bar already carries.

          `navSelected` rather than a fresh alpha, because the sidebar has already measured what
          a brand tint on a surface should weigh (0.12 light / 0.20 dark, with 0.08 and 0.16
          rejected as too subtle and too loud). A second, hand-picked amount here would be the
          same decision made twice with only one of them written down.
        */
        ...(active && {
          borderLeft: `2px solid ${theme.palette.brand[500]}`,
          pl: 4,
          ml: -4,
          // Symmetric, so the wash has the same air on both sides of the text. Padding inside
          // an equal negative margin leaves the content exactly where it was.
          pr: 4,
          mr: -4,
        }),
        backgroundColor: flash ? navSelected(theme) : 'transparent',
        // Under reduced motion the wash still appears and still clears — it just does both
        // instantly. Showing nothing would be the wrong reading of the preference: the request
        // is for no ANIMATION, not for no feedback.
        ...(!reduceMotion && { transition: 'background-color 600ms ease' }),
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={3} sx={{ mb: 1 }}>
        {/* 19/1.4 — the docs' own h3. Each term is a subheading of its section, and this page
            renders inside the docs reader, where `body2` (16) read as body text pretending to be a
            heading. */}
        <Typography sx={{ fontSize: 19, lineHeight: 1.4, fontWeight: 600 }}>
          {entry.term}
        </Typography>
        {entry.aliases && entry.aliases.length > 0 && (
          <Typography sx={{ fontSize: 14 }} color="text.secondary">
            also: {entry.aliases.join(', ')}
          </Typography>
        )}
      </Stack>

      {/* 16/1.75 — the docs prose body, verbatim. This is the sentence people come here to read
          and it was 14, which on a docs page reads as fine print. */}
      <Typography sx={{ fontSize: 16, lineHeight: 1.75 }}>
        {entry.short}
      </Typography>

      {/*
        MARKDOWN, not plain text. `long` may reference another term mid-sentence
        (§44's `[the words](term:id)`), and a bare `Typography` would print that as its
        own source. Here a reference opens the definition in place via `Term`; only
        `TermOverlay` has to route it differently, because it cannot open itself.
      */}
      {entry.long && (
        <TermProse
          text={entry.long}
          sx={{ fontSize: 16, lineHeight: 1.75, mt: 2, color: 'text.secondary' }}
        />
      )}

      {entry.formula && (
        <Box
          component="pre"
          sx={{
            mt: 2,
            p: 3,
            borderRadius: 2,
            // WRAP, don't scroll. `<pre>` defaults to `white-space: pre`, so with only
            // `overflowX: auto` these ran off the right edge and cut mid-word on a narrow
            // viewport ("…less your included allow|"). That is the right treatment for code,
            // where a broken line changes meaning — but these "formulas" are prose sentences
            // describing an arithmetic, and a horizontal scrollbar inside a definition is a
            // worse read than a second line. The monospace box is kept: it still marks the
            // text as a formula rather than commentary.
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            fontSize: 14,
          }}
        >
          {entry.formula}
          {entry.unit ? `   [${entry.unit}]` : ''}
        </Box>
      )}

      {/* `relatedEntries`, not `entry.seeAlso` — the row must reflect what will actually
          render. A term whose every reference sits above this reader's floor has an empty
          Related list, and testing the unfiltered ids would draw the row anyway, with
          nothing in it. Both sides are coerced to booleans as well: `0 && …` renders a
          literal "0" in JSX, which an entry carrying two empty arrays would have hit. */}
      {Boolean(relatedEntries.length || entry.docs?.length) && (
        <Stack direction="row" flexWrap="wrap" spacing={3} sx={{ mt: 2 }}>
          {/*
            PERSISTENT UNDERLINE, not hover-only.

            These were `textDecoration: 'none'` with an underline appearing on hover, so
            colour alone said "link" — and the colour was `brand[400]`, which computed to
            2.34:1 on this page (AA wants 4.5:1 for 14px text). Two problems compounding:
            the affordance was invisible until you happened to be over it, and the one
            signal carrying it was the least legible thing on the page.

            Colour now comes from `brandText` (brandText.ts — brand[600] light / brand[400]
            dark, both ≥4.5:1), and the underline is always on. A chip treatment was the
            alternative and is wrong here: chips are this app's STATUS vocabulary, and a
            dozen of them in one viewport on a dense reference page reads as a tag cloud.
            An underline is the quietest thing that still says "clickable" at rest.

            The guide link gets the same underline. The two sit in one row, and giving only
            one a rest affordance makes the row look half-finished; its `text.secondary`
            colour is what keeps them distinguishable.
          */}
          {relatedEntries.map(related => (
            <Typography
              key={related.id}
              component={RouterLink}
              to={glossaryHref(related.id)}
              // `brandText`, not `brand[400]`. The literal was the DARK value applied in
              // both modes, and it measures 2.45:1 on the light page — a clear AA
              // failure that nothing had caught. Dark is unchanged (still brand[400],
              // 7.78:1); light moves to brand[600] at 4.91:1.
              // Colour from `brandText` — the literal was the DARK value applied in both
              // modes and measured 2.45:1 on the light page, a clear AA failure nothing had
              // caught. Light moves to brand[600] (4.91:1); dark is unchanged at 7.78:1.
              //
              // The UNDERLINE is persistent rather than hover-only, which is the one place
              // this differs from the version that landed on main first. Hover-only leaves
              // COLOUR as the sole rest affordance, and a reader who cannot separate purple
              // from near-black body text then has no way to tell these are clickable at
              // all — the same defect the contrast fix above exists to remove, one step
              // further along. The counter-argument is noise: a dozen underlined items in
              // one viewport on a dense reference page. That is real, which is why the rule
              // is 1px at a 3px offset rather than the browser default — quiet enough to
              // recede, present enough to be an affordance.
              sx={{
                fontSize: 14,
                color: brandText(theme),
                textDecoration: 'underline',
                textDecorationThickness: LINK_UNDERLINE_THICKNESS,
                textUnderlineOffset: LINK_UNDERLINE_OFFSET,
                '&:hover': { color: brandTextHover(theme) },
              }}
            >
              {/* The entry's OWN term, not its id de-hyphenated. `activation-price-tier`
                  rendered as "activation price tier" — lowercase, and only accidentally
                  the same words as the heading it points at. */}
              {related.term}
            </Typography>
          ))}
          {/*
            NAME the guide. This rendered the literal string "guide ↗" for every entry in
            `docs`, which was survivable while every term linked at most one — and became
            "guide ↗  guide ↗" the moment `activation-trigger` gained a second. Two links
            with identical labels are worse than one: the reader cannot tell them apart,
            and the label was contentless even when unambiguous.

            A picker (modal, dropdown) was the alternative and is more machinery than the
            thing it wraps — a control to choose between two links, each of which is one
            click. The links are short; show their names.

            `findDoc` yields the catalog's `title`, the same short label the docs nav uses,
            so a term's guide reads identically here and in the sidebar. When a slug does
            not resolve — an entry pointing at an unpublished or internal guide — fall back
            to the humanised slug rather than dropping the link silently, so the authoring
            mistake is visible on the page instead of hidden.
          */}
          {entry.docs?.map(d => {
            const [category, slug] = d.split('/')
            const title = findDoc(category, slug)?.title ?? slug?.replace(/-/g, ' ') ?? d
            return (
              <Typography
                key={d}
                component={RouterLink}
                to={`/docs/${d}`}
                sx={{
                  fontSize: 14,
                  color: theme.palette.text.secondary,
                  textDecoration: 'underline',
                  textDecorationThickness: LINK_UNDERLINE_THICKNESS,
                  textUnderlineOffset: LINK_UNDERLINE_OFFSET,
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {title} ↗
              </Typography>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

/**
 * A tag's INDEX — its members as links, not as a second copy of each card.
 *
 * ⚠️ LINKS, and this is the constraint that decided the whole shape of the feature. A
 * `TermCard` renders `id={entry.id}`, which is the `#anchor` every tooltip footer deep-links
 * to (§44). Rendering a tagged entry's card here AND in its kind group below would put that
 * id in the document twice — `getElementById` then resolves to whichever came first, so
 * roughly half the glossary's deep links would land on the wrong element with no error
 * anywhere. That is precisely the class of silent failure the arrival-scroll comments on
 * `TermCard` were written about.
 *
 * An index is also the better read on its own terms. The tag answers "what figures does this
 * page show me", which is a question about the SET; the definitions are what you go on to
 * read, one at a time, where they already live.
 */
const TagIndex = ({ entries }: { entries: GlossaryEntry[] }) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        // 12px/8px — a dense two-dimensional list, not a paragraph. `columnGap` is the
        // larger of the two because horizontally adjacent links need more separation to
        // read as separate items than vertically stacked ones do.
        columnGap: 4,
        rowGap: 2,
        pt: 1,
      }}
    >
      {entries.map(entry => (
        <Typography
          key={entry.id}
          component={RouterLink}
          to={glossaryHref(entry.id)}
          // The same rest affordance the `seeAlso` chips carry: `brandText` for AA contrast
          // in both modes, and a persistent 1px underline so colour is not the only signal
          // that these are clickable. See the note on `TermCard`'s related links.
          sx={{
            fontSize: 14,
            color: brandText(theme),
            textDecoration: 'underline',
            textDecorationThickness: LINK_UNDERLINE_THICKNESS,
            textUnderlineOffset: LINK_UNDERLINE_OFFSET,
            '&:hover': { color: brandTextHover(theme) },
          }}
        >
          {entry.term}
        </Typography>
      ))}
    </Box>
  )
}

/**
 * The glossary — one definition per term, and the destination every tooltip footer
 * points at (working-conventions §44).
 *
 * Rendered FROM `docs/glossary.json`, not written as prose, because the same strings feed
 * tooltips, guide references and Ask retrieval. A markdown page would force those three to
 * hold their own copy of the wording, and copies drift without anything noticing.
 */
/**
 * How long the arrival wash stays up before it clears.
 *
 * Long enough to survive the smooth scroll that precedes it (a full-page scroll runs a few
 * hundred ms) and still be lit when the reader's eye arrives; short enough that it has gone by
 * the time they have read the definition. The 600ms fade runs after this, so the mark is fully
 * present for 1.8s and fully gone by 2.4s.
 */
const FLASH_MS = 1800

export const GlossaryPage = () => {
  const theme = useTheme()
  const { hash } = useLocation()
  // Decoded, because the id is what goes in the DOM and `location.hash` is percent-encoded.
  // Every id today is kebab-case ASCII, so this is insurance rather than a live fix — but the
  // fallback if it is ever wrong is silent: no scroll, no mark, and no error to explain it.
  const active = decodeURIComponent(hash.replace(/^#/, ''))
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [flash, setFlash] = useState(false)
  /**
   * Is this the first term this page instance has been asked to land on?
   *
   * ARRIVAL is instant; moving BETWEEN terms afterwards is smooth. The two are different acts.
   * Arriving, the reader has no picture of the page to preserve — they followed a link from
   * somewhere else and want to be where they were sent, which is exactly what the browser's own
   * anchor jump does for a server-rendered page. Animating 4000px of a page they have never seen
   * spends half a second showing them a blur. Once they are here, a `seeAlso` jump is in-page
   * navigation they initiated with the page in front of them, and smooth is what says "you moved
   * within this document" — the same call `DocToc` already makes for a section jump.
   *
   * Robustness pushes the same way and is the part that decided it. A smooth scroll can be
   * interrupted — by a competing scroll, or by an environment that never runs animation frames —
   * and when it is, it fails to exactly the state this whole change exists to fix: the reader at
   * the top of the page with the term below the fold. Measured in the preview pane the
   * walkthroughs are driven in, `requestAnimationFrame` fires zero times and a smooth
   * `scrollIntoView` moves the container not at all, while an instant one lands correctly. The
   * failure that matters is silent, so the act that must not fail does not animate.
   */
  const firstLanding = useRef(true)

  /*
    Land the reader ON the deep-linked term.

    Nothing did this before. `scrollMarginTop` was already on the card, which reads like anchor
    handling and is not: it only shapes a scroll that something else performs. React Router does
    not scroll to a fragment, and the browser's own anchor jump does not fire either, because on
    an in-app navigation there is no document load and on a cold load the element does not exist
    until React has mounted. So the two paths that matter both ended with the page at the top and
    the term somewhere below the fold, marked by a 2px rule the reader never saw.

    Both paths are covered here for the same reason: this effect runs after the commit that
    rendered the cards, so the element exists whether the id arrived with the document or with a
    click. `setTimeout(…, 0)` rather than `requestAnimationFrame` — rAF does not fire at all in
    the preview pane the walkthroughs run in, which would make this look broken exactly where it
    gets verified.
  */
  useEffect(() => {
    if (!active) return
    const el = document.getElementById(active)
    if (!el) return

    const smooth = !reduceMotion && !firstLanding.current
    firstLanding.current = false
    const scroll = window.setTimeout(() => {
      el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' })
    }, 0)
    setFlash(true)
    const clear = window.setTimeout(() => setFlash(false), FLASH_MS)

    return () => {
      window.clearTimeout(scroll)
      window.clearTimeout(clear)
    }
    // Re-runs on every hash change, so following a `seeAlso` link from one term to another
    // scrolls and re-marks rather than leaving the reader where they were.
  }, [active, reduceMotion])

  // No query state: the on-page search field is gone (see the note at the render), so there is
  // nothing to filter BY. Keeping the filter would have left `query` permanently '' — dead code
  // whose only visible effect was an unreachable "Nothing matches ''" state.
  // Staff read the internal corpus here, brands the brand one. The page is the place that
  // matters most for this: a tooltip is reached by someone who already met the word, but
  // the glossary is where you go to find out what vocabulary EXISTS — and an internal term
  // that appears in no list is a term nobody can discover.
  const corpus = useGlossaryCorpus()
  // VRG-680 — the docs nav beside the glossary is the gated customer catalog.
  const { source: customerDocs } = useCustomerDocs()

  const groups = useMemo(
    () =>
      GLOSSARY_KINDS.map(g => ({ ...g, entries: glossaryByKind(g.kind, corpus) }))
        // A kind with nothing in it is a heading over a void. Formulas and columns are
        // genuinely empty until the content sweep, so this is the normal case today, not
        // an edge case.
        .filter(g => g.entries.length > 0),
    [corpus],
  )

  /**
   * The tag indexes, resolved against THIS reader's corpus.
   *
   * Scoped the same way the kind groups are, so a tag that happens to include an
   * `audience: internal` entry lists it for staff and omits it for a brand — the audience
   * gate working, not a hole. Empty ones are dropped for the same reason an empty kind is.
   */
  const tagIndexes = useMemo(
    () =>
      GLOSSARY_TAGS.map(t => ({ ...t, entries: glossaryByTag(t.tag, corpus) })).filter(
        t => t.entries.length > 0,
      ),
    [corpus],
  )

  // Counts the KIND groups only. A tagged entry is already counted in its kind, and the
  // index is a list of links rather than a set of definitions — adding it here would
  // report the corpus as larger than it is and make the empty state unreachable on a
  // glossary whose only content was somehow a tag.
  const total = groups.reduce((n, g) => n + g.entries.length, 0)

  return (
    /*
      Rendered INSIDE the docs reader shell, not as a standalone page.
 
      The glossary is a docs page: it is listed in the docs nav, it lives under /docs, and someone
      arriving from a tooltip is reading documentation. Rendering it full-width meant following that
      nav row threw away the left index, the search field and the three-column measure — you left
      the docs in order to read the docs. Inside the shell the left index IS the back-navigation,
      which is the same answer DocPage gives for guides (and why guides carry no breadcrumb trail).
 
      No `aside`: the right rail is a per-document table of contents, and this page's own search
      plus its kind groupings already do that job.
    */
    <DocsLayout
      source={customerDocs}
      activeValue={GLOSSARY_NAV_VALUE}
      headline="Glossary"
      /*
        The same right-hand rail every guide has, listing this page's SECTIONS (Terms, Statuses, …).
        `DocToc` was generalised from a `DocEntry` to a plain heading list precisely so this could
        reuse it rather than grow a second, drifting copy of the sticky rail and its scroll-spy.
        Depth 2 puts every row at the rail, since the kind sections are siblings.
      */
      aside={
        <DocToc
          // Tag indexes first, matching the render order below — the rail is a map of the
          // page, so a row out of document order sends the scroll-spy backwards.
          headings={[
            ...tagIndexes.map(t => ({ id: t.id, text: t.label, depth: 2 })),
            ...groups.map(g => ({ id: g.kind, text: g.label, depth: 2 })),
          ]}
          scrollKey="glossary"
          onSelect={id => {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }}
        />
      }
    >
    <Stack data-tour="glossary-list" spacing={8}>
      <Stack spacing={3}>
        <Typography color="text.secondary" sx={{ fontSize: 16, lineHeight: 1.75, maxWidth: 640 }}>
          What each word means in Vera. Definitions here are the same ones you see in
          tooltips across the app, so they cannot disagree.
        </Typography>
        {/*
          NO on-page search field.

          There was one, and it was redundant twice over: the docs shell's own field sits ~200px up
          and to the left in this very view, and the ⌘K palette now returns glossary DEFINITIONS
          directly in its results (above Docs), which is a faster answer than filtering a list and
          then reading it. Two search boxes in one viewport, doing subtly different things, is worse
          than one that does more.

          What is lost is in-place filtering of the full list — you can no longer type "cert" and see
          every cert-related term together. That is what the right-hand rail replaces: 14 entries in
          four labelled sections is a browsable list, not one that needs narrowing.
        */}
      </Stack>

      {/* Reachable only if the published glossary is genuinely empty — a build problem, not a
          search miss, so the copy says so instead of blaming a query. */}
      {total === 0 ? (
        <EmptyState
          icon={<BookOpenIcon strokeWidth={1} size={28} />}
          title="No terms published yet"
          text="The glossary is generated from docs/glossary.json. Once entries are published they appear here."
        />
      ) : (
        <>
        {/*
          TAG INDEXES FIRST, above the kind groups.

          A Fragment rather than a wrapper Box, so these stay DOM children of the outer
          `Stack` and keep its `spacing={8}` between sections. A wrapper would collapse
          every index and every kind group into two spaced blocks instead of N.

          Order is deliberate: the indexes answer "what is in here", which is the question
          a reader arriving cold has, and they are short. The definitions they point into
          are below, where a reader following a link from anywhere else lands directly.
        */}
        {tagIndexes.map(index => (
          <Stack key={index.id} spacing={2}>
            <Typography
              id={index.id}
              sx={{
                fontSize: 14,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
                color: 'text.primary',
                scrollMarginTop: `${ANCHOR_OFFSET}px`,
              }}
            >
              {index.label}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 16, lineHeight: 1.75, mb: 1 }}>
              {index.blurb}
            </Typography>
            <TagIndex entries={index.entries} />
          </Stack>
        ))}
        {groups.map(group => (
          <Stack key={group.kind} spacing={2}>
            {/* `id` so the right-hand rail can anchor to it and the scroll-spy can observe it —
                the same contract a guide's headings have. */}
            <Typography
              id={group.kind}
              sx={{
                fontSize: 14,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
                color: 'text.primary',
                scrollMarginTop: `${ANCHOR_OFFSET}px`,
              }}
            >
              {group.label}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 16, lineHeight: 1.75, mb: 1 }}>
              {group.blurb}
            </Typography>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              {group.entries.map(entry => (
                <TermCard
                  key={entry.id}
                  entry={entry}
                  active={entry.id === active}
                  flash={flash && entry.id === active}
                  reduceMotion={reduceMotion}
                />
              ))}
            </Box>
          </Stack>
        ))}
        </>
      )}
    </Stack>
    </DocsLayout>
  )
}
